import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Target, Users, DollarSign, TrendingUp, BarChart3, Loader2,
  ArrowLeft, AlertCircle, Trophy, Percent, Clock, ArrowRight, Sparkles
} from "lucide-react";
import { ValorPonderadoCard, AnaliseIACRMDialog } from "@/components/crm/CRMExtras";

const STAGE_COLORS = {
  contato_cliente: "#3B82F6", diagnostico: "#8B5CF6", visita_tecnica: "#F59E0B",
  propostas: "#10B981", contrato: "#0A1628", venda_fechada: "#059669", venda_perdida: "#EF4444",
};

const STAGE_LABELS_PT = {
  contato_cliente: "Contato com Cliente",
  diagnostico: "Proposta Potencial",
  visita_tecnica: "Visita Técnica",
  propostas: "Propostas",
  contrato: "Contrato",
  venda_fechada: "Venda Fechada",
  venda_perdida: "Venda Perdida",
};

function StatCard({ title, value, subtitle, icon: Icon, color = "#0A1628" }) {
  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "15" }}>
            <Icon className="h-5 w-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ConversionFunnelStage({ stageId, label, count, value, cumulative, maxCumulative, conversionPct, dropoffCount, color, isLast, tempoMedio }) {
  const widthPct = maxCumulative > 0 ? (cumulative / maxCumulative) * 100 : 0;
  return (
    <div className="space-y-1" data-testid={`funnel-stage-${stageId}`}>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
          <span className="font-medium text-slate-700 truncate">{label}</span>
          <Badge variant="outline" className="text-[10px]">{count} nesta etapa</Badge>
        </div>
        <div className="flex items-center gap-3 text-xs shrink-0">
          {tempoMedio != null && (
            <span className="flex items-center gap-1 text-slate-500" title="Tempo médio que os leads ficam nesta etapa (histórico real)" data-testid={`tempo-medio-${stageId}`}>
              <Clock className="h-3 w-3" />⌀ {tempoMedio}d
            </span>
          )}
          {value > 0 && <span className="font-mono text-slate-500">R$ {value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>}
        </div>
      </div>
      <div className="h-9 bg-slate-100 rounded-lg overflow-hidden relative">
        <div
          className="h-full rounded-lg transition-all duration-700 flex items-center justify-between px-3"
          style={{ width: `${Math.max(widthPct, 6)}%`, backgroundColor: color }}
        >
          <span className="text-white text-xs font-bold">{cumulative} {cumulative === 1 ? "lead" : "leads"}</span>
          {widthPct > 25 && conversionPct !== null && (
            <span className="text-white/90 text-[10px] font-semibold">{conversionPct.toFixed(0)}% avançaram</span>
          )}
        </div>
      </div>
      {!isLast && conversionPct !== null && (
        <div className="flex items-center justify-between text-[11px] text-slate-500 px-1 pt-0.5">
          <span className="flex items-center gap-1">
            <ArrowRight className="h-3 w-3" />
            <span className="font-semibold" style={{ color: conversionPct >= 50 ? "#059669" : conversionPct >= 25 ? "#F59E0B" : "#EF4444" }}>
              {conversionPct.toFixed(1)}%
            </span>
            <span className="text-slate-400">conversão para a próxima etapa</span>
          </span>
          {dropoffCount > 0 && (
            <span className="text-red-500">-{dropoffCount} leads perdidos no caminho</span>
          )}
        </div>
      )}
    </div>
  );
}

export default function CRMDashboardPage() {
  const [data, setData] = useState(null);
  const [funil, setFunil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iaOpen, setIaOpen] = useState(false);
  const [iaLoading, setIaLoading] = useState(false);
  const [analise, setAnalise] = useState(null);
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchFunil = () => axios.get(`${API}/crm/funil-avancado`, { headers }).then(r => setFunil(r.data)).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/crm/dashboard`, { headers })
      .then(r => setData(r.data))
      .catch(() => toast.error("Erro ao carregar dashboard"))
      .finally(() => setLoading(false));
    fetchFunil();
    axios.get(`${API}/crm/analise-ia`, { headers }).then(r => { if (r.data?.existe !== false) setAnalise(r.data); }).catch(() => {});
  }, []);

  const analisarIA = async () => {
    setIaLoading(true); setIaOpen(true);
    try {
      const r = await axios.post(`${API}/crm/analisar-ia`, {}, { headers });
      setAnalise(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro na análise de IA");
      setIaOpen(false);
    } finally { setIaLoading(false); }
  };

  if (loading) return <MainLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#0A1628]" /></div></MainLayout>;
  if (!data) return <MainLayout><p className="text-center text-slate-500 py-12">Erro ao carregar dados</p></MainLayout>;

  const pipelineStages = ["contato_cliente", "visita_tecnica", "propostas", "diagnostico", "contrato", "venda_fechada"];
  // Cumulative: how many leads reached or passed this stage (this + later)
  const cumulativeByStage = {};
  for (let i = 0; i < pipelineStages.length; i++) {
    let total = 0;
    for (let j = i; j < pipelineStages.length; j++) {
      total += data.por_etapa?.[pipelineStages[j]]?.total || 0;
    }
    cumulativeByStage[pipelineStages[i]] = total;
  }
  const maxCumulative = Math.max(...Object.values(cumulativeByStage), 1);
  // Best/worst conversion (between adjacent stages)
  let bestConv = { label: "-", pct: 0 };
  let worstConv = { label: "-", pct: 100 };
  for (let i = 1; i < pipelineStages.length; i++) {
    const prev = cumulativeByStage[pipelineStages[i - 1]];
    const cur = cumulativeByStage[pipelineStages[i]];
    if (prev > 0) {
      const pct = (cur / prev) * 100;
      const stepLabel = `${STAGE_LABELS_PT[pipelineStages[i - 1]]} → ${STAGE_LABELS_PT[pipelineStages[i]]}`;
      if (pct > bestConv.pct) bestConv = { label: stepLabel, pct };
      if (pct < worstConv.pct) worstConv = { label: stepLabel, pct };
    }
  }
  const motivos = Object.entries(data.motivos_perda || {}).sort((a, b) => b[1] - a[1]);
  const ranking = Object.entries(data.ranking_responsaveis || {}).sort((a, b) => b[1].valor_vendido - a[1].valor_vendido);

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="crm-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#0A1628]">Dashboard Comercial</h1>
            <p className="text-sm text-slate-500">Visao geral do pipeline de vendas</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => { if (analise) { setIaOpen(true); } else { analisarIA(); } }} disabled={iaLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="btn-analisar-funil-ia">
              <Sparkles className="h-4 w-4 mr-1" />Analisar funil com IA
            </Button>
            <Link to="/comercial/crm"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Voltar ao CRM</Button></Link>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard title="Total de Leads" value={data.total_leads} icon={Users} color="#3B82F6" />
          <StatCard title="Taxa de Conversao" value={`${data.taxa_conversao}%`} icon={Percent} color="#10B981" subtitle={`${(data.total_contratos || 0) + (data.total_vendas_fechadas || 0)} conv. (contrato+fechado)`} />
          <StatCard title="Em Propostas" value={`R$ ${(data.total_propostas_valor || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} icon={DollarSign} color="#F59E0B" />
          <StatCard title="Total em Contrato" value={`R$ ${(data.total_vendido || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} icon={TrendingUp} color="#0A1628" subtitle={`${data.total_contratos || 0} contratos`} />
          <StatCard title="Vendas Fechadas" value={`R$ ${(data.total_fechado || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} icon={Trophy} color="#059669" subtitle={`${data.total_vendas_fechadas || 0} vendas concluídas`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversion Funnel */}
          <Card className="border-0 shadow-sm lg:col-span-2" data-testid="conversion-funnel-card">
            <CardHeader>
              <CardTitle className="text-base text-[#0A1628] flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />Funil de Conversão
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">Largura = leads que atingiram a etapa. % indica avanço para a próxima.</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {pipelineStages.map((s, idx) => {
                const stage = data.por_etapa?.[s];
                if (!stage) return null;
                const cumulative = cumulativeByStage[s];
                const nextCumulative = idx < pipelineStages.length - 1 ? cumulativeByStage[pipelineStages[idx + 1]] : null;
                const conversionPct = nextCumulative !== null && cumulative > 0 ? (nextCumulative / cumulative) * 100 : null;
                const dropoffCount = nextCumulative !== null ? cumulative - nextCumulative : 0;
                return (
                  <ConversionFunnelStage
                    key={s}
                    stageId={s}
                    label={STAGE_LABELS_PT[s]}
                    count={stage.total}
                    value={stage.valor}
                    cumulative={cumulative}
                    maxCumulative={maxCumulative}
                    conversionPct={conversionPct}
                    dropoffCount={dropoffCount}
                    color={STAGE_COLORS[s]}
                    isLast={idx === pipelineStages.length - 1}
                    tempoMedio={funil?.tempo_medio_dias?.[s]}
                  />
                );
              })}
              {/* Best/Worst conversion summary */}
              <Separator />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100" data-testid="best-conversion-step">
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-semibold uppercase tracking-wide">
                    <TrendingUp className="h-3.5 w-3.5" />Melhor passagem
                  </div>
                  <p className="text-sm font-medium text-emerald-900 mt-1">{bestConv.label}</p>
                  <p className="text-xl font-bold text-emerald-600">{bestConv.pct.toFixed(1)}%</p>
                </div>
                <div className="p-3 rounded-lg bg-red-50 border border-red-100" data-testid="worst-conversion-step">
                  <div className="flex items-center gap-1.5 text-xs text-red-700 font-semibold uppercase tracking-wide">
                    <AlertCircle className="h-3.5 w-3.5" />Maior gargalo
                  </div>
                  <p className="text-sm font-medium text-red-900 mt-1">{worstConv.label}</p>
                  <p className="text-xl font-bold text-red-600">{worstConv.pct.toFixed(1)}%</p>
                </div>
              </div>
              {/* Venda Perdida (off-funnel) */}
              {(data.por_etapa?.venda_perdida?.total || 0) > 0 && (
                <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                  <span className="flex items-center gap-1.5">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STAGE_COLORS.venda_perdida }} />
                    Venda Perdida (fora do funil)
                  </span>
                  <Badge variant="outline" className="text-red-600 border-red-200">
                    {data.por_etapa.venda_perdida.total} leads
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pipeline ponderado + Prioridades */}
          <div className="space-y-6">
            <ValorPonderadoCard funil={funil} onReload={fetchFunil} />
            <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-base text-[#0A1628]">Por Prioridade</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: "urgente", label: "Urgente", color: "bg-red-500" },
                { key: "alta", label: "Alta", color: "bg-amber-500" },
                { key: "media", label: "Media", color: "bg-blue-500" },
                { key: "baixa", label: "Baixa", color: "bg-slate-400" },
              ].map(p => (
                <div key={p.key} className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><div className={`h-3 w-3 rounded-full ${p.color}`} /><span className="text-sm">{p.label}</span></div>
                  <span className="text-sm font-bold">{data.por_prioridade?.[p.key] || 0}</span>
                </div>
              ))}
            </CardContent>
            </Card>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ranking Vendedores */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-base text-[#0A1628] flex items-center gap-2"><Trophy className="h-5 w-5 text-[#C9A84C]" />Ranking de Vendedores</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {ranking.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Sem dados</p>}
              {ranking.map(([nome, stats], i) => (
                <div key={nome} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-[#C9A84C] text-white" : i === 1 ? "bg-slate-300 text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{nome}</p>
                    <p className="text-xs text-slate-500">{stats.total} leads / {stats.convertidos} convertidos</p>
                  </div>
                  <p className="text-sm font-mono font-medium text-emerald-600">R$ {stats.valor_vendido.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Motivos de Perda */}
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle className="text-base text-[#0A1628] flex items-center gap-2"><AlertCircle className="h-5 w-5 text-red-500" />Motivos de Perda</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {motivos.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Sem vendas perdidas</p>}
              {motivos.map(([motivo, count]) => (
                <div key={motivo} className="flex items-center justify-between p-2 bg-red-50/50 rounded">
                  <span className="text-sm">{motivo}</span>
                  <Badge variant="outline" className="text-red-600 border-red-200">{count}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Origem dos Leads */}
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-base text-[#0A1628]">Origem dos Leads</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(data.por_origem || {}).sort((a, b) => b[1] - a[1]).map(([origem, count]) => {
                const labels = { indicacao: "Indicacao", site: "Site", telefone: "Telefone", whatsapp: "WhatsApp", email: "E-mail", redes_sociais: "Redes Sociais", visita: "Visita", outro: "Outro", nao_informado: "Nao informado" };
                return (
                  <div key={origem} className="px-4 py-2 rounded-full border bg-white flex items-center gap-2">
                    <span className="text-sm">{labels[origem] || origem}</span>
                    <Badge variant="outline" className="text-xs">{count}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <AnaliseIACRMDialog open={iaOpen} onClose={() => setIaOpen(false)} loading={iaLoading}
          analise={analise} onAnalisar={analisarIA} />
      </div>
    </MainLayout>
  );
}
